import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import { getOptionsGroupedByParentKey } from 'utils/getOptionsGroupedByParentKey';

import { FormattedOption } from 'types/dictionaries';
import { TranslationKey } from 'types/i18next';

import { Dictionaries } from 'constants/enums';

import SelectCommon from '../common/SelectCommon/SelectCommon';
import { SelectValue } from '../filter.types';

interface SkillsetSelectProps {
  value: SelectValue;
  onChange?: (value: SelectValue) => void;
  placeholder?: string;
  disabled?: boolean;
  title?: TranslationKey;
  hasAccessToFilter?: boolean;
}

const SkillsetSelect: FC<SkillsetSelectProps> = ({
  value,
  onChange,
  title = 'common.label.skillset',
  hasAccessToFilter = false,
  ...restProps
}) => {
  const { t } = useTranslation();
  const skillsetDict = useSelector(
    selectDictionary(Dictionaries.Skillset)
  ) as FormattedOption[];

  const industryDict = useSelector(
    selectDictionary(Dictionaries.Industry)
  ) as FormattedOption[];

  const skillsetOptions = useMemo(
    () =>
      getOptionsGroupedByParentKey({
        itemsList: skillsetDict,
        labelKey: ['label'],
        valueKey: ['value'],
        parentKey: ['parentKey'],
        parentKeyMappingDict: industryDict
      }),
    [skillsetDict, industryDict]
  );

  const handleChange = useCallback(
    (value: SelectValue) => {
      onChange?.(value);
    },
    [onChange]
  );

  return (
    <SelectCommon
      title={t(title)}
      value={value}
      onChange={handleChange}
      customOptions={skillsetOptions}
      placeholder={t('common.label.selectSkillset')}
      hasAccessToFilter={hasAccessToFilter}
      {...restProps}
    />
  );
};

export default SkillsetSelect;
